import React, { useState, useEffect } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  CardTitle,
  Container,
  FormFeedback,
  CardHeader,
} from "reactstrap"
import Select from "react-select"
import Dropzone from "react-dropzone"
import * as Yup from "yup"
import { useFormik } from "formik"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import SuccessPopup from "../../../components/SuccessPopup"
import ErrorPopup from "../../../components/ErrorPopup"
import {
  fetchPvsCategory,
  fetchPvsSubcategory,
  fetchProductDetails,
  updateProduct,
} from "../../../redux/actions/pvs"
import { UPDATE_PRODUCT_RESET } from "../../../redux/types/pvs"
import { toast } from "react-toastify"

const EditProduct = () => {
  document.title = "Edit Product | PVS | Admin Dashboard"

  const history = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { product } = location.state

  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [subcategoryOptions, setSubcategoryOptions] = useState([])
  const [productData, setProductData] = useState()

  const pvsState = useSelector(state => state.pvs)
  const updateProductState = useSelector(
    state => state.screens.pvs.updateProduct
  )

  // Options for Select
  const categoryOptions = (pvsState.categoryList || []).map(c => ({
    value: c.pvs_cat_id,
    label: c.category,
    encId: c.enc_pvs_cat_id,
  }))

  // Helpers to convert comma-separated strings to objects
  const getObjects = (arr, options) => {
    if (!arr) return []
    if (typeof arr === "string") arr = arr.split(",").map(s => s.trim())
    if (!Array.isArray(arr)) return []
    return arr
      .map(val => options.find(opt => opt.value == String(val)))
      .filter(Boolean)
  }

  // Formik setup
  const validation = useFormik({
    initialValues: {
      product_name: "",
      category: null,
      subcategory: null,
    },
    validationSchema: Yup.object({
      product_name: Yup.string().required("Please Enter Product Name"),
      category: Yup.object().required("Please Select Category"),
      subcategory: Yup.object().required("Please Select Subcategory"),
    }),
    onSubmit: values => {
      console.log("values..", values)

      let requestData = {
        enc_pvs_product_id: productData.enc_product_id,
        product_name: values.product_name,
        enc_pvs_cat_id: values.category.encId,
        enc_pvs_subcat_id: values.subcategory.encId,
      }
      dispatch(updateProduct(requestData))
    },
  })

  // Handle category change to fetch subcategory
  const handleCategoryChange = selectedCategory => {
    validation.setFieldValue("category", selectedCategory)
    validation.setFieldValue("subcategory", []) // Reset subcategory

    if (selectedCategory) {
      dispatch(
        fetchPvsSubcategory({
          enc_pvs_cat_id: selectedCategory.encId,
        })
      )
    } else {
      setSubcategoryOptions([])
    }
  }

  useEffect(() => {
    dispatch(fetchPvsCategory())
  }, [])

  // Set fetched product data
  useEffect(() => {
    if (product) {
      setProductData(product)
    }
  }, [product])

  useEffect(() => {
    // Fetch subcategory for the product's category
    if (productData) {
      validation.setValues({
        product_name: productData.product_name,
        category: categoryOptions.find(
          c => c.value === Number(productData.pvs_cat_id)
        ),
        subcategory: null,
      })
      const category = categoryOptions.find(
        c => c.value === Number(productData.pvs_cat_id)
      )
      if (category) {
        dispatch(
          fetchPvsSubcategory({
            enc_pvs_cat_id: category.encId,
          })
        )
      }
    }
  }, [productData])

  useEffect(() => {
    if (validation.values.category) {
      dispatch(
        fetchPvsSubcategory({
          enc_pvs_cat_id: validation.values.category.encId,
        })
      )
    }
  }, [validation.values.category])

  // Update subcategory options when fetched
  useEffect(() => {
    if (pvsState.subcategoryList && pvsState.subcategoryList.length > 0) {
      const tempSubcategoryOption = pvsState.subcategoryList.map(option => ({
        label: option.subcategory,
        value: option.pvs_subcat_id,
        encId: option.enc_pvs_subcat_id,
      }))
      setSubcategoryOptions(tempSubcategoryOption)
    }
  }, [pvsState.subcategoryList])

  useEffect(() => {
    if (productData && subcategoryOptions.length > 0) {
      console.log("subcategoryOptions..", subcategoryOptions)
      console.log("productData..", productData)

      let subcategory = subcategoryOptions.find(
        c => c.value === productData.pvs_subcat_id
      )
      console.log("subcategory..", subcategory)

      validation.setFieldValue("subcategory", subcategory)
    }
  }, [subcategoryOptions, productData])

  // Handle update success/error popups
  useEffect(() => {
    if (updateProductState.success) {
      toast.success(
        updateProductState.message || "Product updated successfully!"
      )
      dispatch({ type: UPDATE_PRODUCT_RESET })
      history("/product-list", { replace: true })
    } else if (updateProductState.error) {
      setErrorMsg(updateProductState.message || "Unable to update product.")
      setErrorOpen(true)
    }
  }, [updateProductState])

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false)
          // dispatch({ type: RESET_UPDATE_PRODUCT_STATE })
          history("/product-list", { replace: true })
        }}
        message={updateProductState?.message || "Product updated successfully!"}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false)
          // dispatch({ type: RESET_UPDATE_PRODUCT_STATE })
        }}
        message={errorMsg}
      />
      <div className="page-content">
        <Container fluid={true}>
          <div className="flex-grow-1">
            <h4 className="h4 nom-title mt-4 mb-4">Edit Product</h4>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                    action="#"
                  >
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="category">
                          Category <span className="text-danger">*</span>
                        </Label>
                        <Select
                          name="category"
                          value={validation.values.category}
                          onChange={handleCategoryChange}
                          options={categoryOptions}
                          classNamePrefix="select2-selection"
                          placeholder="Select Category"
                        />
                        {validation.touched.category &&
                        validation.errors.category ? (
                          <div className="text-danger">
                            {validation.errors.category}
                          </div>
                        ) : null}
                      </div>

                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="subcategory">
                          Subcategory <span className="text-danger">*</span>
                        </Label>
                        <Select
                          name="subcategory"
                          value={validation.values.subcategory}
                          onChange={selectedOptions =>
                            validation.setFieldValue(
                              "subcategory",
                              selectedOptions
                            )
                          }
                          options={subcategoryOptions}
                          classNamePrefix="select2-selection"
                          placeholder="Select Subcategory"
                        />
                        {validation.touched.subcategory &&
                        validation.errors.subcategory ? (
                          <div className="text-danger">
                            {validation.errors.subcategory}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="product_name">
                          Product Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="product_name"
                          className="form-control"
                          placeholder="Enter Product Name"
                          type="text"
                          id="product_name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.product_name}
                          invalid={
                            validation.touched.product_name &&
                            !!validation.errors.product_name
                          }
                        />
                        {validation.touched.product_name &&
                        validation.errors.product_name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.product_name}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <Button type="submit" color="primary">
                        Update Product
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default EditProduct
